import { Link } from "react-router-dom";
import { Lock, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const ENTITLEMENT_META = {
  ai_cfo: { label: "AI CFO", description: "Cash runway, invoices, budgets and real-time financial advice." },
  ai_marketing: { label: "AI Marketing manager", description: "Campaigns, content calendar, social posts and ad copy on tap." },
  ai_seo: { label: "AI SEO specialist", description: "Website audits, keyword research and SEO-optimised posts." },
  accounting_sync: { label: "Accounting sync", description: "Pull live numbers from QuickBooks Online or Xero." },
  email_briefing: { label: "Morning briefing email", description: "A friendly 7 AM briefing with cash alerts, delivered to your inbox." },
};

export function useEntitlement(key) {
  const { user } = useAuth();
  const ent = user?.entitlements;
  if (!ent) return { allowed: true, status: null, planName: null };
  return { allowed: !!ent.entitlements?.[key], status: ent.status, planName: ent.plan_name };
}

export function UpgradeGate({ entitlement, children }) {
  const { allowed, status, planName } = useEntitlement(entitlement);
  if (allowed) return children;
  const meta = ENTITLEMENT_META[entitlement] || { label: entitlement, description: "" };
  const expired = status === "trial_expired" || status === "canceled";
  return (
    <div data-testid="upgrade-gate" className="bg-card border border-border rounded-2xl p-8 sm:p-12 text-center max-w-2xl mx-auto my-6">
      <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
        <Lock className="h-6 w-6 text-primary" />
      </div>
      <h2 className="font-display font-semibold text-2xl mt-5" data-testid="upgrade-gate-title">
        {expired ? "Your access has ended" : `Upgrade to unlock ${meta.label}`}
      </h2>
      <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
        {expired
          ? `Your ${status === "trial_expired" ? "free trial" : "subscription"} ran out, so ${meta.label} is paused. Pick a plan to turn it back on.`
          : `You're currently on ${planName || "a plan"} which doesn't include ${meta.label}. ${meta.description}`}
      </p>
      <div className="mt-6 flex items-center justify-center gap-2">
        <Button asChild size="lg" data-testid="upgrade-gate-cta">
          <Link to="/settings?tab=billing">
            <Sparkles className="h-4 w-4 mr-1.5" />
            See plans <ArrowRight className="h-4 w-4 ml-1.5" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
